import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { NavigationProvider } from './contexts/NavigationContext';
import Navigation from './components/Navigation';
import Dashboard from './pages/Dashboard';
import PatientList from './pages/PatientList';
import PatientDashboard from './pages/PatientDashboard';
import Sessions from './pages/Sessions';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Biometrics from './pages/Biometrics';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import PatientEdit from './pages/PatientEdit';
import PatientAdd from './pages/PatientAdd';
import PatientStaffAssignment from './pages/PatientStaffAssignment';
import BehaviorDetails from './pages/BehaviorDetails';
import AddBehavior from './pages/AddBehavior';
import Chat from './pages/Chat';
import ParentList from './pages/ParentList';
import ParentAdd from './pages/ParentAdd';
import ParentDetails from './pages/ParentDetails';
import ParentAssignments from './pages/ParentAssignments';
import ParentDashboard from './pages/ParentDashboard';
import ParentChildDetails from './pages/ParentChildDetails';
import ParentLayout from './components/ParentLayout';
import UserManagement from './pages/UserManagement';
import AuditLogs from './pages/AuditLogs';
import Organizations from './pages/Organizations';
import EmailLogs from './pages/EmailLogs';
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ConsentAcceptance from './pages/ConsentAcceptance';
import PrivacyCenter from './pages/PrivacyCenter';
import DataCorrectionRequest from './pages/DataCorrectionRequest';
import DataCorrectionAdmin from './pages/DataCorrectionAdmin';
import SessionWarning from './components/SessionWarning';
import VersionFooter from './components/VersionFooter';
import { useSessionMonitor } from './hooks/useSessionMonitor';
import api from './services/api';

// Token-based auth check
const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  return !!token;
};

// Protected Route wrapper
const ProtectedRoute = () => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
};

// Consent guard - redirects to /consent if user hasn't accepted required terms
const ConsentGuard = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [consentsOk, setConsentsOk] = useState(false);

  useEffect(() => {
    const checkConsent = async () => {
      try {
        const res = await api.get('/lgpd/consent-status');
        const missing = res.data?.data?.missingConsents || [];
        if (missing.length > 0) {
          navigate('/consent', { replace: true });
        } else {
          setConsentsOk(true);
        }
      } catch (err) {
        // If consent check fails, let the user through rather than blocking
        console.error('Consent check failed:', err);
        setConsentsOk(true);
      } finally {
        setChecking(false);
      }
    };
    checkConsent();
  }, [navigate]);

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-700"></div>
      </div>
    );
  }

  return consentsOk ? <Outlet /> : null;
};

// Layout component for authenticated routes
const AuthenticatedLayout = () => {
  // Session monitoring hook - tracks inactivity timeout
  const { sessionWarning, minutesRemaining, extendSession, lockSession } = useSessionMonitor();

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Session Warning Banner */}
      {sessionWarning && (
        <SessionWarning
          minutesRemaining={minutesRemaining}
          onExtend={extendSession}
          onLogout={lockSession}
        />
      )}

      <div className="flex-shrink-0 h-full">
        <Navigation />
      </div>
      <div className="flex-1 min-w-0 overflow-auto flex flex-col">
        <div className="p-6 flex-1">
          <Outlet />
        </div>
        <VersionFooter />
      </div>
    </div>
  );
};



// Custom theme for GiA
const theme = extendTheme({
  colors: {
    brand: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
    },
  },
  fonts: {
    heading: 'Inter, system-ui, sans-serif',
    body: 'Inter, system-ui, sans-serif',
  },
});

const App = () => {
  return (
    <ChakraProvider theme={theme}>
      <Router>
        <NavigationProvider>
          <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/esqueci-minha-senha" element={<ForgotPassword />} />
          <Route path="/redefinir-senha" element={<ResetPassword />} />
          <Route path="/" element={<Navigate to="/landing" replace />} />

          {/* Consent acceptance (requires auth but no sidebar) */}
          <Route element={<ProtectedRoute />}>
            <Route path="/consent" element={<ConsentAcceptance />} />
          </Route>

          {/* Protected routes - with consent check */}
          <Route element={<ProtectedRoute />}>
            {/* Change Password - accessible without sidebar navigation */}
            <Route path="/change-password" element={<ChangePassword />} />
            <Route element={<ConsentGuard />}>
            <Route element={<AuthenticatedLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/patients" element={<PatientList />} />
              <Route path="/dashboard/patients/new" element={<PatientAdd />} />
              <Route path="/dashboard/patients/:id" element={<PatientDashboard />} />
              <Route path="/dashboard/patients/:id/edit" element={<PatientEdit />} />
              <Route path="/dashboard/patients/:id/staff" element={<PatientStaffAssignment />} />
              <Route path="/dashboard/patients/:id/sessions/:sessionId" element={<PatientDashboard />} />
              <Route path="/dashboard/sessions" element={<Sessions />} />
              <Route path="/dashboard/analytics" element={<Analytics />} />
              <Route path="/dashboard/settings" element={<Settings />} />
              <Route path="/dashboard/biometrics" element={<Biometrics />} />
              <Route path="/dashboard/onboarding" element={<Onboarding />} />
              <Route path="/dashboard/chat" element={<Chat />} />
              <Route path="/dashboard/user-management" element={<UserManagement />} />
              <Route path="/dashboard/audit-logs" element={<AuditLogs />} />
              <Route path="/dashboard/organizations" element={<Organizations />} />
              <Route path="/dashboard/emails" element={<EmailLogs />} />
              <Route path="/dashboard/privacy" element={<PrivacyCenter />} />
              <Route path="/dashboard/privacy/correction-request" element={<DataCorrectionRequest />} />
              <Route path="/dashboard/privacy/corrections" element={<DataCorrectionAdmin />} />
              <Route path="/dashboard/patients/:patientId/behaviors/new" element={<AddBehavior />} />
              <Route path="/dashboard/patients/:patientId/behaviors/:behaviorId" element={<BehaviorDetails />} />
              
              {/* Parent Management Routes */}
              <Route path="/dashboard/parents" element={<ParentList />} />
              <Route path="/dashboard/parents/new" element={<ParentAdd />} />
              <Route path="/dashboard/parents/:id" element={<ParentDetails />} />
              <Route path="/dashboard/parents/:id/edit" element={<ParentAdd />} />
              <Route path="/dashboard/parents/:parentId/assignments" element={<ParentAssignments />} />
              
              {/* Parent-Specific Routes */}
              <Route path="/parent" element={<ParentLayout />}>
                <Route path="dashboard" element={<ParentDashboard />} />
                <Route path="children/:childId" element={<ParentChildDetails />} />
              </Route>
            </Route>
            </Route>
          </Route>

          {/* Catch all route - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </NavigationProvider>
    </Router>
    </ChakraProvider>
  );
};

export default App; 